import { useState, useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import teamImage from '../../../../assets/images/courses/team-players/team.jpg';
import introImg from '../../../../assets/images/courses/teamplayer&collaboration/chapter12.1.jpg';
import {
  course_sorting_questions,
  course_questions as courseQuestions,
} from '../../../../courses/building-teamwork-collaboration/building-teamwork-collaboration-quiz';
import { CourseChapter } from '../../../../courses/courses.types';
import {
  CourseQuestionAnswer,
  CourseSortingQuestion,
  QuestionOption,
  ShortingMatchingBase,
} from '../../../../courses/jumpstart-communicator/jumpstart-communicator.types';
import { useUserCourseQuestionResponseContext } from '../../../../hooks/use-user-course-question-response.hooks';
import {
  dispatchUpdateUserCourseQuestionResponse,
  UserChapterQuestion,
} from '../../../../redux/slice/user-course-question-response';
import { useAppDispatch } from '../../../../redux/store';
import { CourseQuestionCard } from '../../CourseQuestionCard';
import { MediaBlock } from '../../MediaBlock';
import { CourseSortingQuestionBlock } from '../../SortingQuestion/CourseSortingQuestion';

interface TeamPlayersAndCollaborationChapter1 {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

type SortingAnswersState = {
  [questionId: string]: ShortingMatchingBase[];
};

type AnswersState = {
  [question: string]: CourseQuestionAnswer;
};

export const TeamPlayersAndCollaborationChapter12: React.FC<
  TeamPlayersAndCollaborationChapter1
> = ({ chapter, onNextChapter, totalPages }) => {
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  const [openPopup, setOpenPopup] = useState<string | null>(null);
  const [isPageDirty, setIsPageDirty] = useState(true);
  const [, setAnswers] = useState<AnswersState>({});

  const chapterQuestions = courseQuestions.filter(
    (courseChapter) => courseChapter.chapter_id === chapter.id,
  );

  const popupRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const courseSortingQuestion = course_sorting_questions.filter(
    (courseChapter) => courseChapter.chapter_id === chapter.id,
  );
  const userCourseQuestionResponseContext = useUserCourseQuestionResponseContext();

  const handleCourseAnswerSubmit = async (
    quizAnswers: QuestionOption[],
    isCorrect: boolean,
    questionId?: string,
  ) => {
    setAnswers((prevAnswers) => {
      if (questionId) {
        return {
          ...prevAnswers,
          [questionId]: {
            chapter_id: chapter.id,
            question_id: questionId,
            question: chapterQuestions[0].question,
            is_correct: isCorrect,
            answer_options: quizAnswers,
          },
        };
      } else {
        return { ...prevAnswers };
      }
    });

    const userQuestionAnswer: UserChapterQuestion = {
      question_id: questionId,
      isCorrect: isCorrect,
      question: chapterQuestions.find((x) => x.id === questionId)?.question,
      answers: quizAnswers,
    };
    const userAnswerResponse =
      userCourseQuestionResponseContext[chapterQuestions[0].course_id]?.chapters[chapter.id]
        ?.questions || [];

    await dispatch(
      dispatchUpdateUserCourseQuestionResponse({
        [chapterQuestions[0].course_id]: {
          chapters: {
            [chapter.id]: {
              questions: [
                ...userAnswerResponse.filter((x) => x.question_id !== questionId),
                userQuestionAnswer,
              ],
              quiz: null,
            },
          },
        },
      }),
    );
  };

  const handleQuestionRetake = async (questionId: string) => {
    setAnswers((prevAnswers) => {
      //eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [questionId]: _, ...newState } = prevAnswers;
      return newState;
    });
    const userAnswerResponse =
      userCourseQuestionResponseContext[chapterQuestions[0].course_id]?.chapters[chapter.id]
        ?.questions || [];

    await dispatch(
      dispatchUpdateUserCourseQuestionResponse({
        [chapterQuestions[0].course_id]: {
          chapters: {
            [chapter.id]: {
              questions: [...userAnswerResponse.filter((x) => x.question_id !== questionId)],
              quiz: null,
            },
          },
        },
      }),
    );
    setIsPageDirty(true);
  };

  const handleSortingExcerciseCompleted = async (sortingAnswers: SortingAnswersState) => {
    const userSortingAnswerResponse =
      userCourseQuestionResponseContext[courseSortingQuestion[0].course_id]?.chapters[chapter.id]
        ?.sortingQuestions || [];

    await dispatch(
      dispatchUpdateUserCourseQuestionResponse({
        [courseSortingQuestion[0].course_id]: {
          chapters: {
            [chapter.id]: {
              sortingQuestions: [
                ...userSortingAnswerResponse.filter(
                  (question) => !Object.keys(question).includes(Object.keys(sortingAnswers)[0]),
                ),
                sortingAnswers,
              ],
              quiz: null,
            },
          },
        },
      }),
    );
  };
  const handleSortingRetake = async (questionId: string) => {
    const userSortingAnswerResponse =
      userCourseQuestionResponseContext[courseSortingQuestion[0].course_id]?.chapters[chapter.id]
        ?.sortingQuestions || [];

    await dispatch(
      dispatchUpdateUserCourseQuestionResponse({
        [courseSortingQuestion[0].course_id]: {
          chapters: {
            [chapter.id]: {
              sortingQuestions: [
                ...userSortingAnswerResponse.filter(
                  (question) => !Object.keys(question).includes(questionId),
                ),
              ],
              quiz: null,
            },
          },
        },
      }),
    );
    setIsPageDirty(true);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setOpenPopup(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpenPopup = (popupType: string) => {
    setOpenPopup(openPopup === popupType ? null : popupType);
  };

  useEffect(() => {
    const useCourseQuestionResponse =
      userCourseQuestionResponseContext[chapterQuestions[0].course_id]?.chapters[chapter.id]
        ?.questions || [];
    const userSortingAnswerResponse =
      userCourseQuestionResponseContext[courseSortingQuestion[0].course_id]?.chapters[chapter.id]
        ?.sortingQuestions;
    if (
      Object.keys(userCourseQuestionResponseContext).length &&
      userSortingAnswerResponse?.length &&
      useCourseQuestionResponse?.length
    ) {
      setIsPageDirty(false);
    }
  }, [userCourseQuestionResponseContext]);
  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Team Behaviors to Avoid</h2>
        <br />
        <MediaBlock mediaImage={introImg}></MediaBlock>
        <br />
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Being a good team player means making allowances for people who want to do things
          differently to you, but it also means doing what's best for the team, even if you must get
          a bit unpopular at times.
        </p>
        <br />
        <p className="text-lg text-center font-semibold mt-10 mb-10">
          For example, if one of the others in your team isn't being a team player, then you should
          challenge them on that. Here are the seven worst behaviors in a team, and they should be
          addressed if you notice them, including if it's you.
        </p>
        <p className="text-lg text-left font-semibold mt-10 mb-10">
          Click the seven markers below to learn more.
        </p>
        <div className="relative max-w-[100%] md:max-w-[70%] mx-auto">
          <div className="relative max-w-[100%] items-center justify-center mt-10">
            <img src={teamImage} alt="Team" />
            <div className="absolute top-0 w-full h-full">
              <div className="absolute top-[32%] left-[7%]">
                <div className="relative w-full">
                  <button
                    key="communication"
                    className={`w-6 h-6 flex items-center justify-center rounded-full text-gray-600 transition-all duration-300 ease-in-out
                      hover:shadow-[0_6px_16px_rgba(0,0,0,0.3)]
                      ${
                        openPopup === 'communication'
                          ? 'bg-purple-500 text-white'
                          : 'bg-white shadow-[0_6px_16px_rgba(0,0,0,0.4)]'
                      }
                    `}
                    onClick={() => handleOpenPopup('communication')}
                  >
                    +
                  </button>

                  {/* Popup inside div */}
                  {openPopup === 'communication' && (
                    <div
                      ref={popupRef}
                      className="absolute top-0 left-8 bg-white p-4 z-50 rounded-lg graph-popup"
                    >
                      <span className="left-arrow-span arrow-span"></span>
                      <h4 className="font-medium text-sm my-2">Lack of Communication</h4>
                      <p className="text-xs font-light text-black my-2">
                        Lack of communication is a biggie. Even if you're introverted, being part of
                        a team means participating and sharing important information. If someone's
                        not communicating, help by asking for their input, but if it continues,
                        address it.
                      </p>
                      <button
                        className="text-gray-600 mt-2 absolute top-0 right-2"
                        onClick={() => setOpenPopup(null)}
                      >
                        X
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="absolute top-[24%] left-[30%]">
                <div className="relative w-full">
                  <button
                    key="laziness"
                    className={`w-6 h-6 flex items-center justify-center rounded-full text-gray-600 transition-all duration-300 ease-in-out
                      hover:shadow-[0_6px_16px_rgba(0,0,0,0.3)]
                      ${
                        openPopup === 'laziness'
                          ? 'bg-purple-500 text-white'
                          : 'bg-white shadow-[0_6px_16px_rgba(0,0,0,0.4)]'
                      }
                    `}
                    onClick={() => handleOpenPopup('laziness')}
                  >
                    +
                  </button>

                  {/* Popup inside div */}
                  {openPopup === 'laziness' && (
                    <div
                      ref={popupRef}
                      className="absolute top-0 left-8 bg-white p-4 z-50 rounded-lg graph-popup"
                    >
                      <span className="left-arrow-span arrow-span"></span>
                      <h4 className="font-medium text-sm my-2">Laziness</h4>
                      <p className="text-xs font-light text-black my-2">
                        Laziness, or not pulling your weight, is a problem. If you notice someone
                        being lazy, ask if they need help. But if it keeps happening, call them out
                        on it.
                      </p>
                      <button
                        className="text-gray-600 mt-2 absolute top-0 right-2"
                        onClick={() => setOpenPopup(null)}
                      >
                        X
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="absolute top-[20%] left-[60%]">
                <div className="relative w-full">
                  <button
                    key="dishonesty"
                    className={`w-6 h-6 flex items-center justify-center rounded-full text-gray-600 transition-all duration-300 ease-in-out
                      hover:shadow-[0_6px_16px_rgba(0,0,0,0.3)]
                      ${
                        openPopup === 'dishonesty'
                          ? 'bg-purple-500 text-white'
                          : 'bg-white shadow-[0_6px_16px_rgba(0,0,0,0.4)]'
                      }
                    `}
                    onClick={() => handleOpenPopup('dishonesty')}
                  >
                    +
                  </button>

                  {/* Popup inside div */}
                  {openPopup === 'dishonesty' && (
                    <div
                      ref={popupRef}
                      className="absolute top-0 right-8 bg-white p-4 z-50 rounded-lg graph-popup"
                    >
                      <span className="right-arrow-span arrow-span"></span>
                      <h4 className="font-medium text-sm my-2">Dishonesty</h4>
                      <p className="text-xs font-light text-black my-2">
                        Dishonesty is another key issue. Everyone in the team must be honest with
                        each other, even when it's tough. That means speaking up when you know
                        something important.
                      </p>
                      <button
                        className="text-gray-600 mt-2 absolute top-0 right-2"
                        onClick={() => setOpenPopup(null)}
                      >
                        X
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="absolute top-[37%] right-[25%]">
                <div className="relative w-full">
                  <button
                    key="attitude"
                    className={`w-6 h-6 flex items-center justify-center rounded-full text-gray-600 transition-all duration-300 ease-in-out
                      hover:shadow-[0_6px_16px_rgba(0,0,0,0.3)]
                      ${
                        openPopup === 'attitude'
                          ? 'bg-purple-500 text-white'
                          : 'bg-white shadow-[0_6px_16px_rgba(0,0,0,0.4)]'
                      }
                    `}
                    onClick={() => handleOpenPopup('attitude')}
                  >
                    +
                  </button>

                  {/* Popup inside div */}
                  {openPopup === 'attitude' && (
                    <div
                      ref={popupRef}
                      className="absolute top-0 right-8 bg-white p-4 z-50 rounded-lg graph-popup"
                    >
                      <span className="right-arrow-span arrow-span"></span>
                      <h4 className="font-medium text-sm my-2">Negative Attitude</h4>
                      <p className="text-xs font-light text-black my-2">
                        Having a negative attitude about the situation or team members, including
                        gossip, is also harmful. If you've got negative thoughts, keep them to
                        yourself.
                      </p>
                      <button
                        className="text-gray-600 mt-2 absolute top-0 right-2"
                        onClick={() => setOpenPopup(null)}
                      >
                        X
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="absolute top-[55%] left-[7%]">
                <div className="relative w-full">
                  <button
                    key="aggressiveness"
                    className={`w-6 h-6 flex items-center justify-center rounded-full text-gray-600 transition-all duration-300 ease-in-out
                      hover:shadow-[0_6px_16px_rgba(0,0,0,0.3)]
                      ${
                        openPopup === 'aggressiveness'
                          ? 'bg-purple-500 text-white'
                          : 'bg-white shadow-[0_6px_16px_rgba(0,0,0,0.4)]'
                      }
                    `}
                    onClick={() => handleOpenPopup('aggressiveness')}
                  >
                    +
                  </button>

                  {/* Popup inside div */}
                  {openPopup === 'aggressiveness' && (
                    <div
                      ref={popupRef}
                      className="absolute top-0 left-8 bg-white p-4 z-50 rounded-lg graph-popup"
                    >
                      <span className="left-arrow-span arrow-span"></span>
                      <h4 className="font-medium text-sm my-2">Aggressiveness</h4>
                      <p className="text-xs font-light text-black my-2">
                        Aggressive behavior toward team members is a big no-no, as is its sneaky
                        cousin, passive-aggressive behavior. This includes sulking, ignoring team
                        members, holding grudges, and gossiping behind people's backs.
                      </p>
                      <button
                        className="text-gray-600 mt-2 absolute top-0 right-2"
                        onClick={() => setOpenPopup(null)}
                      >
                        X
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="absolute bottom-[25%] left-[47%]">
                <div className="relative w-full">
                  <button
                    key="selfishness"
                    className={`w-6 h-6 flex items-center justify-center rounded-full text-gray-600 transition-all duration-300 ease-in-out
                      hover:shadow-[0_6px_16px_rgba(0,0,0,0.3)]
                      ${
                        openPopup === 'selfishness'
                          ? 'bg-purple-500 text-white'
                          : 'bg-white shadow-[0_6px_16px_rgba(0,0,0,0.4)]'
                      }
                    `}
                    onClick={() => handleOpenPopup('selfishness')}
                  >
                    +
                  </button>

                  {/* Popup inside div */}
                  {openPopup === 'selfishness' && (
                    <div
                      ref={popupRef}
                      className="absolute top-0 left-8 bg-white p-4 z-50 rounded-lg graph-popup"
                    >
                      <span className="left-arrow-span arrow-span"></span>
                      <h4 className="font-medium text-sm my-2">Selfishness</h4>
                      <p className="text-xs font-light text-black my-2">
                        Selfishness tops the list, meaning putting yourself before the team, which
                        harms everyone. It's like not returning favors that others have done for
                        you. This can quickly break down the team spirit.
                      </p>
                      <button
                        className="text-gray-600 mt-2 absolute top-0 right-2"
                        onClick={() => setOpenPopup(null)}
                      >
                        X
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="absolute bottom-[27%] right-[25%]">
                <div className="relative w-full">
                  <button
                    key="unreliability"
                    className={`w-6 h-6 flex items-center justify-center rounded-full text-gray-600 transition-all duration-300 ease-in-out
                      hover:shadow-[0_6px_16px_rgba(0,0,0,0.3)]
                      ${
                        openPopup === 'unreliability'
                          ? 'bg-purple-500 text-white'
                          : 'bg-white shadow-[0_6px_16px_rgba(0,0,0,0.4)]'
                      }
                    `}
                    onClick={() => handleOpenPopup('unreliability')}
                  >
                    +
                  </button>

                  {/* Popup inside div */}
                  {openPopup === 'unreliability' && (
                    <div
                      ref={popupRef}
                      className="absolute top-0 right-8 bg-white p-4 z-50 rounded-lg graph-popup"
                    >
                      <span className="right-arrow-span arrow-span"></span>
                      <h4 className="font-medium text-sm my-2">Unreliability</h4>
                      <p className="text-xs font-light text-black my-2">
                        Unreliability is next, which means not keeping your promises or doing what
                        you agreed to do for the team. Keeping promises is vital, especially when
                        others are counting on you. It builds trust, the foundation of teamwork.
                      </p>
                      <button
                        className="text-gray-600 mt-2 absolute top-0 right-2"
                        onClick={() => setOpenPopup(null)}
                      >
                        X
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <br />
        <h3 className="text-3xl text-purple-600 font-bold mt-10 m-5">Scenario Activity: </h3>
        <p className="text-lg font-normal text-left mt-5 mb-5">
          Match the behavioral scenario examples below by dragging and dropping each under the
          behavior the scenario is demonstrating.
        </p>
        <br />
        <table className="w-full border-collapse border  border-gray-300">
          <thead>
            <tr>
              <th className="border">Behavior to Avoid</th>
              <th>Scenario</th>
            </tr>
          </thead>
          <tbody>
            <tr className="font-normal">
              <td className="border p-2 font-light text-lg">Selfishness</td>
              <td className="border p-2 font-light text-lg">
                During a group project, Sarah consistently takes credit for the team's ideas and
                contributions, failing to acknowledge the efforts of her teammates. This behavior
                creates resentment among the team members, leading to a breakdown in trust and
                cooperation.
              </td>
            </tr>

            <tr>
              <td className="border p-2 font-light text-lg">Unreliability</td>
              <td className="border p-2 font-light text-lg">
                James agrees to complete a crucial task by a specific deadline but repeatedly fails
                to deliver on time, causing delays in the project timeline. His teammates feel
                frustrated and disappointed by his lack of reliability, impacting the team's overall
                productivity and morale.
              </td>
            </tr>
            <tr>
              <td className="border p-2 font-light text-lg">Negative Attitude</td>
              <td className="border p-2 font-light text-lg">
                Emily consistently complains about the team's direction and criticizes her
                colleagues behind their backs, spreading negativity within the group. Her
                pessimistic attitude affects team morale and hinders collaboration, making it
                challenging to achieve common goals.
              </td>
            </tr>
            <tr>
              <td className="border p-2 font-light text-lg">Aggressive Behavior</td>
              <td className="border p-2 font-light text-lg">
                During a team meeting, Mark criticizes his colleague's ideas in a hostile and
                confrontational manner, undermining their confidence and creating a tense
                atmosphere. Other team members feel uncomfortable speaking up, fearing Mark's
                aggressive reactions.
              </td>
            </tr>
            <tr>
              <td className="border p-2 font-light text-lg">Dishonesty</td>
              <td className="border p-2 font-light text-lg">
                During a project presentation, John exaggerates the team's achievements and glosses
                over important issues to impress stakeholders. His dishonesty leads to
                misunderstandings and erodes trust within the team, jeopardizing the project's
                success.
              </td>
            </tr>
            <tr>
              <td className="border p-2 font-light text-lg">Laziness</td>
              <td className="border p-2 font-light text-lg">
                David routinely procrastinates on his assigned tasks and relies on his teammates to
                pick up the slack, causing resentment and frustration among the group. Despite
                repeated reminders, David fails to contribute effectively, impacting the team's
                overall performance.
              </td>
            </tr>
            <tr>
              <td className="border p-2 font-light text-lg">Lack of Communication</td>
              <td className="border p-2 font-light text-lg">
                Sarah, a key team member, consistently fails to provide updates on her progress or
                share important information with the rest of the team. Her lack of communication
                leads to confusion and misunderstandings, hindering collaboration and productivity.
              </td>
            </tr>
          </tbody>
        </table>
        {/* pending */}
        <DndProvider backend={HTML5Backend}>
          <CourseSortingQuestionBlock
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onRetake={handleSortingRetake}
            question={courseSortingQuestion[0] as CourseSortingQuestion}
            submitedCorrectAnswerCount={
              userCourseQuestionResponseContext[courseSortingQuestion[0].course_id]?.chapters[
                chapter.id
              ]?.sortingQuestions
                ?.filter((x) => Object.keys(x).filter((key) => key === courseSortingQuestion[0].id))
                .map(
                  (x) =>
                    x[courseSortingQuestion[0].id].filter(
                      (x) => x.answer?.id === x.correct_answer?.id,
                    ).length,
                )[0] ?? null
            }
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            submitSortingQuestion={handleSortingExcerciseCompleted}
          />
        </DndProvider>
        <p className="text-lg font-semibold text-left mt-5 mb-5">
          It’s a good idea to ask yourself if there's one of the above behaviors that you are a
          little bit prone to. Maybe that could be a good resolution to reduce that tendency in
          yourself and become an even better team player.
        </p>
        <h3 className="text-3xl text-purple-600 font-bold mt-10 m-5">Summary</h3>
        <p className="text-lg font-normal text-left mt-5 mb-5">
          Remember, being part of a team is all about supporting each other and working together
          towards a common goal. By avoiding these negative behaviors and embracing kindness and
          collaboration, you can help create a positive and supportive team environment where
          everyone feels valued and respected. Keep listening, showing empathy, offering assistance,
          and celebrating successes – you've got this!
        </p>
        <p className="text-2xl text-center font-normal mt-5 mb-10">
          Keep listening, showing empathy, offering assistance, and celebrating successes – you've
          got this!
        </p>
        <h3 className="text-3xl text-purple-600 font-bold mt-10 m-5">
          Activity: How Can You Deal with Conflict?
        </h3>

        <p className="text-lg font-bold flex-1 mt-5 mb-5">
          Below are some situations that may cause conflict. Select the best solution for each
          situation.
        </p>

        <CourseQuestionCard
          submittedAnswer={userCourseQuestionResponseContext[
            chapterQuestions[0].course_id
          ]?.chapters[chapter.id]?.questions?.find((x) => x.question_id === chapterQuestions[0].id)}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmitAnswer={handleCourseAnswerSubmit}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onRetakeQuestion={handleQuestionRetake}
          question={chapterQuestions[0]}
        />
        <br />
        <CourseQuestionCard
          submittedAnswer={userCourseQuestionResponseContext[
            chapterQuestions[0].course_id
          ]?.chapters[chapter.id]?.questions?.find((x) => x.question_id === chapterQuestions[1].id)}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmitAnswer={handleCourseAnswerSubmit}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onRetakeQuestion={handleQuestionRetake}
          question={chapterQuestions[1]}
        />
        <br />
        <CourseQuestionCard
          submittedAnswer={userCourseQuestionResponseContext[
            chapterQuestions[0].course_id
          ]?.chapters[chapter.id]?.questions?.find((x) => x.question_id === chapterQuestions[2].id)}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmitAnswer={handleCourseAnswerSubmit}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onRetakeQuestion={handleQuestionRetake}
          question={chapterQuestions[2]}
        />
        <br />
        <CourseQuestionCard
          submittedAnswer={userCourseQuestionResponseContext[
            chapterQuestions[3].course_id
          ]?.chapters[chapter.id]?.questions?.at(3)}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmitAnswer={handleCourseAnswerSubmit}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onRetakeQuestion={handleQuestionRetake}
          question={chapterQuestions[3]}
        />
        <br />
        <p className="text-2xl text-center font-normal mt-5 mb-10">
          Ready to level up your team player and collaboration skills? The next section provides a
          tool you can use to take action.
        </p>
        <button
          disabled={isPageDirty}
          onClick={handleNextChapterClick}
          className="btn-internal w-full"
        >
          Take Action
        </button>
      </div>
    </>
  );
};
